import { Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../common/hooks';
import { logout, toggleSessionExpiredModal } from '../../../features/auth/authSlice';
import OktaLoginWidget from '../../../features/login-okta-widget';
import { BasicButton } from '../../atoms';
import { AlertDialog } from '../../molecules/AlertDialog/AlertDialog';

export const GridContainer: any = styled(Grid)(() => ({
    minHeight: 'calc(100vh - 28px)'
}));

export const LeftContainer: any = styled(GridContainer)(({ theme }) => ({
    '.MuiInputBase-input': {
        boxSizing: 'border-box'
    },
    backgroundColor: theme.palette.layout.primary,
    alignItems: 'flex-start',
    justifyContent: 'center',
    display: 'flex',
    flexDirection: 'column',
    padding: '0 10%'
}));

export const RightContainer: any = styled(GridContainer)(({ theme }) => ({
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    padding: '20px',
    justifyContent: 'end',
    backgroundColor: theme.palette.layout.primary
}));

export const ErrorSpan = styled(Typography)(({ theme }) => ({
    color: theme.palette.berry[400],
    display: 'flex',
    marginTop: '5px'
}));

export const ItemContainer = styled('div')(({ theme }) => ({
    marginTop: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    '& a': {
        color: theme.palette.background.paper
    }
}));

export const FormContainer: any = styled(GridContainer)(() => ({
    '.MuiInputBase-input': {
        boxSizing: 'border-box'
    },
    padding: 30,
    borderRadius: 10,
    maxWidth: '330px',
    minHeight: '75vh'
}));

export const Button = styled(BasicButton)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    minWidth: '35%',
    color: theme.palette.primary[500],
    textTransform: 'capitalize'
}));

export const WelcomText = styled(Typography)(({ theme }) => ({
    color: theme.palette.background.default
}));

interface FormInput {
    username: string;
    password: string;
    rememberMe: boolean;
}

export const LoginForm = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const showSessionExpiredModal = useAppSelector((state) => state.auth.showSessionExpiredModal);
    const [showModal, setShowModal] = useState(showSessionExpiredModal);
    const currentLanguage = useAppSelector((state) => state.global.currentLanguage || 'en');

    const { setValue } = useForm<FormInput>({
        mode: 'onBlur',
        reValidateMode: 'onChange',
        defaultValues: {
            username: '',
            password: '',
            rememberMe: false
        }
    });

    useEffect(() => {
        dispatch(logout());
        (async () => {
            const email = await localStorage.getItem('GOE_APP_REMEMBERED_EMAIL');

            if (email) {
                setValue('username', email);
                setValue('rememberMe', true);
            }
        })();
    }, []);

    const handleCloseModal = () => {
        dispatch(toggleSessionExpiredModal(false));
        setShowModal(false);
    };

    return (
        <GridContainer container>
            <LeftContainer item xs={12} sm={12} md={7} lg={7}>
                <WelcomText variant="h2">{t('TEXT_LOGIN_SCREEN_WELCOME_TEXT_1')}</WelcomText>
                <WelcomText variant="h1" sx={{ fontWeight: 600 }}>
                    {t('TEXT_LOGIN_SCREEN_WELCOME_TEXT_2')}
                </WelcomText>
                <WelcomText variant="h5">{t('TEXT_LOGIN_SCREEN_WELCOME_TEXT_3')}</WelcomText>
            </LeftContainer>

            <RightContainer item xs={12} sm={12} md={5} lg={5}>
                <FormContainer container direction="column">
                    <OktaLoginWidget currentLanguage={currentLanguage} />
                </FormContainer>
            </RightContainer>

            <AlertDialog
                handleClose={handleCloseModal}
                open={showModal}
                content={`${t('SESSION_EXPIRED_MODAL_CONTENT')}`}
                title={t('SESSION_EXPIRED_MODAL_TITLE')}
                aria-label="close-session-expired-popup"
                data-testid="session-expired-modal-close-button"
            />
        </GridContainer>
    );
};
